import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Square, X, Clock, User as UserIcon } from "lucide-react";
import { useTimer, formatDuration } from "@/context/TimerContext";
import api from "@/lib/api";

/**
 * TopBarTimer — persistent timer widget lives in AppLayout's header.
 * When idle, shows a "Start timer" pill that opens a lightweight prompt for
 * task+description. When running, ticks HH:MM:SS with Stop / Discard buttons.
 */
export default function TopBarTimer() {
  const { active, elapsed, start, stop, discard } = useTimer();
  const [creating, setCreating] = useState(false);
  const [task, setTask] = useState("");
  const [description, setDescription] = useState("");
  const [rate, setRate] = useState("");
  const navigate = useNavigate();

  if (!active) {
    return (
      <>
        <button
          data-testid="topbar-timer-start"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--peak-border)] hover:border-[var(--peak-ink)] text-xs font-bold uppercase tracking-widest transition-colors"
        >
          <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
          <span className="hidden md:inline">Start timer</span>
        </button>
        {creating && (
          <QuickTimerModal
            initial={{ task, description, rate }}
            onCancel={() => setCreating(false)}
            onStart={async (payload) => {
              await start(payload);
              setCreating(false);
              setTask(""); setDescription(""); setRate("");
            }}
          />
        )}
      </>
    );
  }

  return (
    <div
      data-testid="topbar-timer-running"
      className="inline-flex items-center gap-2 pl-2 pr-1 py-1 border-2 border-[#008A27] bg-white shadow-[2px_2px_0_0_var(--peak-shadow)]"
    >
      <div className="w-2 h-2 rounded-full bg-[#008A27] animate-pulse" />
      <button
        onClick={() => navigate("/time")}
        className="text-left min-w-0 max-w-[220px] hover:opacity-70"
        title="Open time page"
        data-testid="topbar-timer-label"
      >
        <div className="font-mono font-bold text-sm leading-tight tabular-nums">
          {formatDuration(elapsed)}
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--peak-muted)] leading-tight truncate">
          {active.contact_name || active.task || "Working"}
          {active.billable ? ` · $${active.hourly_rate}/hr` : ""}
        </div>
      </button>
      <button
        onClick={stop}
        data-testid="topbar-timer-stop"
        title="Stop & log this session"
        className="w-7 h-7 flex items-center justify-center bg-[#008A27] hover:bg-black text-white transition-colors"
      >
        <Square className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <button
        onClick={() => { if (window.confirm("Discard this session? Nothing will be recorded.")) discard(); }}
        data-testid="topbar-timer-discard"
        title="Discard without recording"
        className="w-6 h-6 flex items-center justify-center text-[var(--peak-muted)] hover:text-[#E50000] transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

function QuickTimerModal({ initial, onCancel, onStart }) {
  const [task, setTask] = useState(initial.task || "");
  const [description, setDescription] = useState(initial.description || "");
  const [rate, setRate] = useState(initial.rate || "");
  const [contact, setContact] = useState(null);      // { id, name, email, hourly_rate }
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const rateWasAutoFilled = useRef(false);

  // Debounced fetch for the contact typeahead
  useEffect(() => {
    if (!contactOpen || contact) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { q: contactQuery } });
        setContactResults(Array.isArray(data) ? data.slice(0, 8) : []);
      } catch { setContactResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [contactQuery, contactOpen, contact]);

  const pickContact = (c) => {
    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.company || "Contact";
    setContact({ id: c.id, name, email: c.email, hourly_rate: c.hourly_rate });
    setContactOpen(false);
    setContactResults([]);
    setContactQuery("");
    // Auto-fill the rate ONLY if the user hasn't typed one themselves yet.
    if (c.hourly_rate != null && (rate === "" || rateWasAutoFilled.current)) {
      setRate(String(c.hourly_rate));
      rateWasAutoFilled.current = true;
    }
  };

  const clearContact = () => {
    setContact(null);
    if (rateWasAutoFilled.current) {
      setRate("");
      rateWasAutoFilled.current = false;
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onStart({
      contact_id: contact?.id || null,
      task: task.trim(),
      description: description.trim(),
      hourly_rate: rate === "" ? null : parseFloat(rate),
    });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 md:p-8" onMouseDown={onCancel}>
      <form
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="quick-timer-modal"
        className="w-full max-w-md bg-white border-2 border-[var(--peak-ink)] shadow-[8px_8px_0_0_var(--peak-shadow)] mt-16"
      >
        <div className="px-5 py-4 border-b-2 border-[var(--peak-ink)] bg-[var(--peak-surface-alt)] flex items-center justify-between">
          <div className="font-heading font-black text-2xl tracking-tighter uppercase">Start timer</div>
          <button type="button" onClick={onCancel} className="hover:text-[#E50000]"><X className="w-5 h-5" strokeWidth={2.5} /></button>
        </div>
        <div className="p-5 space-y-3">
          {/* Contact picker */}
          <div className="relative">
            <label className="section-label">Related contact (optional)</label>
            {contact ? (
              <div
                className="flex items-center gap-2 border-2 border-[var(--peak-ink)] bg-[var(--peak-surface-alt)] px-2 py-2"
                data-testid="quick-timer-contact-chip"
              >
                <div className="w-6 h-6 bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)] flex items-center justify-center font-heading font-black text-xs flex-none">
                  {(contact.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold truncate">{contact.name}</div>
                  {contact.hourly_rate != null && (
                    <div className="text-[10px] font-mono text-[var(--peak-muted)]">Rate ${contact.hourly_rate}/hr</div>
                  )}
                </div>
                <button type="button" onClick={clearContact} data-testid="quick-timer-contact-clear" className="text-[var(--peak-muted)] hover:text-[#E50000]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <input
                  data-testid="quick-timer-contact-input"
                  value={contactQuery}
                  onChange={(e) => { setContactQuery(e.target.value); setContactOpen(true); }}
                  onFocus={() => setContactOpen(true)}
                  className="brutalist-input"
                  placeholder="Search a client by name, email or company…"
                />
                {contactOpen && contactResults.length > 0 && (
                  <div
                    className="absolute z-10 left-0 right-0 mt-1 bg-white border-2 border-[var(--peak-ink)] shadow-[3px_3px_0_0_var(--peak-shadow)] max-h-56 overflow-y-auto"
                    data-testid="quick-timer-contact-results"
                  >
                    {contactResults.map((c) => {
                      const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.company || "Contact";
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => pickContact(c)}
                          data-testid={`quick-timer-contact-option-${c.id}`}
                          className="w-full text-left px-3 py-2 border-t border-[var(--peak-border)] first:border-t-0 hover:bg-[var(--peak-surface-alt)] flex items-center gap-2"
                        >
                          <UserIcon className="w-3.5 h-3.5 text-[var(--peak-muted)] flex-none" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-bold truncate">{name}</div>
                            {c.email && <div className="text-[10px] font-mono text-[var(--peak-muted)] truncate">{c.email}</div>}
                          </div>
                          {c.hourly_rate != null && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#008A27]">${c.hourly_rate}/hr</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="section-label">Task (optional)</label>
            <input
              data-testid="quick-timer-task"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              className="brutalist-input"
              placeholder="e.g. Design review"
            />
          </div>
          <div>
            <label className="section-label">Notes (optional)</label>
            <textarea
              data-testid="quick-timer-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-[var(--peak-border)] p-2 focus:outline-none focus:border-[var(--peak-ink)]"
              placeholder="What are you working on?"
            />
          </div>
          <div>
            <label className="section-label">Hourly rate (optional)</label>
            <input
              data-testid="quick-timer-rate"
              type="number" min="0" step="0.01"
              value={rate}
              onChange={(e) => { rateWasAutoFilled.current = false; setRate(e.target.value); }}
              className="brutalist-input"
              placeholder="e.g. 75"
            />
            <div className="text-[10px] text-[var(--peak-muted)] mt-1">Leave blank for a non-billable entry.</div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onCancel} className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest border border-[var(--peak-ink)] hover:bg-[var(--peak-ink)] hover:text-white">Cancel</button>
            <button
              type="submit"
              data-testid="quick-timer-submit"
              className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest bg-[#008A27] hover:bg-black text-white inline-flex items-center gap-2"
            >
              <Play className="w-3 h-3" /> Start
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
